import { Empty } from "antd";
import { useEffect, useState } from "react";

import { Loader } from "@fluentui/react-northstar";
import { useTranslation } from "react-i18next";
import SearchAndFilter from "../../../../components/Common/SearchFilter";
import { useTrustedNetwork } from "../../../../context/TrustedNetworkContext";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import "./index.scss";
import SchoolCard from "./SchoolCard";

// Custom hook for responsive behavior
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1200,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};
const EmptyState = ({ type }) => (
  <div className="empty-state">
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        type === "filter"
          ? "No options available"
          : "No schools match your criteria"
      }
    />
  </div>
);

export const Schools = (props) => {
  const { t } = useTranslation();
  const translation = t("trustedNetwork").schools;
  const { getAllSchools, schools, isLoadingSchools, generateTrustRequest } =
    useTrustedNetwork();
  const [searchText, setSearchText] = useState("");
  const [filteredSchools, setFilteredSchools] = useState([]);
  const { width } = useWindowSize();

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    targetSchoolId: null,
  });
  const [selectedFilters, setSelectedFilters] = useState({
    addresses: [],
    cities: [],
    countries: [],
    levels: [],
    categories: [],
    types: [],
  });

  const filterOptions = [
    {
      id: "addresses",
      title: translation.addresses,
      label: schools
        ? [...new Set(schools.map((s) => s.SchoolAddress).filter(Boolean))]
        : [],
      search: true,
    },
    {
      id: "cities",
      title: translation.cities,
      label: schools
        ? [...new Set(schools.map((s) => s.City).filter(Boolean))]
        : [],
      search: true,
    },
    {
      id: "countries",
      title: translation.countries,
      label: schools
        ? [...new Set(schools.map((s) => s.Country).filter(Boolean))]
        : [],
      search: true,
    },
    {
      id: "levels",
      title: translation.levels,
      label: schools
        ? [...new Set(schools.map((s) => s.Level).filter(Boolean))]
        : [],
      search: true,
    },
    {
      id: "categories",
      title: translation.categories,
      label: schools
        ? [...new Set(schools.map((s) => s.Category).filter(Boolean))]
        : [],
      search: true,
    },
    {
      id: "types",
      title: translation.types,
      label: schools
        ? [...new Set(schools.map((s) => s.Type).filter(Boolean))]
        : [],
      search: true,
    },
  ];

  const filterKeyMap = {
    addresses: "SchoolAddress",
    cities: "City",
    countries: "Country",
    levels: "Level",
    categories: "Category",
    types: "Type",
  };

  // Filter handlers for the reusable component
  const handleClearAllFilters = () => {
    setSelectedFilters({
      addresses: [],
      cities: [],
      countries: [],
      levels: [],
      categories: [],
      types: [],
    });
  };

  useEffect(() => {
    getAllSchools();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (schools) {
      filterSchools(searchText, selectedFilters);
    }
  }, [schools, searchText, selectedFilters]);

  const filterSchools = (search, filters) => {
    let filtered = [...schools];

    if (search) {
      filtered = filtered.filter((school) =>
        school.School.toLowerCase().includes(search.toLowerCase())
      );
    }

    Object.keys(filters).forEach((filterId) => {
      const selected = filters[filterId];
      const schoolKey = filterKeyMap[filterId];
      if (selected && selected.length > 0 && schoolKey) {
        filtered = filtered.filter((school) =>
          selected.includes(school[schoolKey])
        );
      }
    });

    setFilteredSchools(filtered);
  };

  const handleFilterChange = (type, value, checked) => {
    const newFilters = { ...selectedFilters };
    // Ensure the filter type exists as an array
    if (!newFilters[type]) {
      newFilters[type] = [];
    }
    if (checked) {
      newFilters[type] = [...newFilters[type], value];
    } else {
      newFilters[type] = newFilters[type].filter((item) => item !== value);
    }
    setSelectedFilters(newFilters);
  };

  const getActiveFiltersCount = () => {
    return Object.values(selectedFilters).reduce(
      (sum, arr) => sum + arr.length,
      0
    );
  };

  const handleTrustRequest = (targetSchoolId) => {
    setConfirmDialog({ open: true, targetSchoolId });
  };

  const handleConfirmRequest = async () => {
    if (confirmDialog.targetSchoolId) {
      await generateTrustRequest(confirmDialog.targetSchoolId);
    }
    setConfirmDialog({ open: false, targetSchoolId: null });
  };

  const handleCancelRequest = () => {
    setConfirmDialog({ open: false, targetSchoolId: null });
  };

  if (isLoadingSchools) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <Loader size="medium" />
      </div>
    );
  }

  if (!schools || schools.length === 0) {
    return (
      <div
        className="empty-state-container"
        style={{ textAlign: "center", padding: "40px 0" }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={translation.noSchoolsFound || "No schools available"}
        />
      </div>
    );
  }

  return (
    <div className="schools-container">
      {/* Centered Search and Filter Bar */}
      <div
        style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}
      >
        <div style={{ minWidth: 320, maxWidth: 600, width: "100%" }}>
          <SearchAndFilter
            searchValue={searchText}
            onSearchChange={setSearchText}
            searchPlaceholder={translation.searchPlaceholder}
            filterOptions={filterOptions}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onClearAllFilters={handleClearAllFilters}
            translations={{
              filter: translation.filter,
              clear: translation.clear || "Clear",
              apply: translation.apply || "Apply",
              search: translation.search,
              notFound: translation.notFound || "No options found",
            }}
          />
        </div>
      </div>
      <div className="schools-content">
        {/* Modern View - matches screenshot exactly */}
        <div
          style={{
            maxHeight: width <= 1099 ? "70vh" : "none",
            overflowY: width <= 1099 ? "auto" : "visible",
            paddingRight: width <= 1099 ? "8px" : "0",
          }}
        >
          <div style={{ padding: "0 8px 8px 8px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  width <= 500
                    ? "repeat(auto-fit, minmax(200px, 1fr))"
                    : "repeat(auto-fit, minmax(250px, 380px))",
                gap: "clamp(6px, 1vw, 16px)",
                justifyContent: "center",
              }}
            >
              {(filteredSchools.length > 0 ? filteredSchools : schools).map(
                (school) => (
                  <SchoolCard
                    key={`card-${school.OwnStgUrl || school.School}`}
                    school={school}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        open={confirmDialog.open}
        onConfirm={handleConfirmRequest}
        onCancel={handleCancelRequest}
        title={translation.confirmRequest}
        content={translation.confirmRequestMessage}
        confirmButton={{
          content: translation.sendRequest,
          primary: true,
          disabled: false,
        }}
        cancelButton={{
          content: translation.cancel,
        }}
      />
    </div>
  );
};
